import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { X, ArrowLeft } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import Modal from 'components/_UI/Modal';
import useMediaQuery from 'hooks/useMediaQuery';
import StepBasicInfo from './form/steps/StepBasicInfo';
import StepDiscount from './form/steps/StepDiscount';
import StepSchedule from './form/steps/StepSchedule';
import StepProducts from './form/steps/StepProducts';
import promotionsApi from 'api/requests/promotions';
import { panelAccent } from 'theme/panelAccent';

const EMPTY_FORM = {
    name: '',
    type: 'percentage',
    is_active: true,
    max_uses: '',
    discount_value: '',
    buy_menu_product_id: null,
    buy_quantity: '',
    get_menu_product_id: null,
    get_quantity: '',
    schedule: {
        schedule_type: 'recurring',
        starts_at: '',
        ends_at: '',
        days_of_week: [],
        time_start: '',
        time_end: '',
        valid_from: '',
        valid_until: '',
    },
    product_ids: [],
};

function toDatetimeLocal(isoString) {
    if (!isoString) return '';
    const d = new Date(isoString);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function promotionToFormData(p) {
    return {
        name: p.name || '',
        type: p.type || 'percentage',
        is_active: p.is_active ?? true,
        max_uses: p.max_uses ?? '',
        discount_value: p.discount_value ?? '',
        buy_menu_product_id: p.buy_menu_product_id ?? null,
        buy_quantity: p.buy_quantity ?? '',
        get_menu_product_id: p.get_menu_product_id ?? null,
        get_quantity: p.get_quantity ?? '',
        schedule: {
            schedule_type: p.schedule?.schedule_type || 'recurring',
            starts_at: toDatetimeLocal(p.schedule?.starts_at),
            ends_at: toDatetimeLocal(p.schedule?.ends_at),
            days_of_week: p.schedule?.days_of_week || [],
            time_start: (p.schedule?.time_start || '').slice(0, 5),
            time_end: (p.schedule?.time_end || '').slice(0, 5),
            valid_from: p.schedule?.valid_from || '',
            valid_until: p.schedule?.valid_until || '',
        },
        product_ids: p.products?.map(pr => pr.id) || [],
    };
}

function buildPayload(formData) {
    const { name, type, is_active, max_uses, discount_value,
        buy_menu_product_id, buy_quantity, get_menu_product_id, get_quantity,
        schedule, product_ids } = formData;

    const s = schedule;
    const schedulePayload = s.schedule_type === 'one_time'
        ? { schedule_type: 'one_time', starts_at: s.starts_at, ends_at: s.ends_at }
        : {
            schedule_type: 'recurring',
            days_of_week: s.days_of_week,
            time_start: s.time_start,
            time_end: s.time_end,
            valid_from: s.valid_from || null,
            valid_until: s.valid_until || null,
        };

    const payload = {
        name,
        type,
        is_active,
        max_uses: max_uses !== '' ? Number(max_uses) : null,
        schedule: schedulePayload,
    };

    if (type === 'bundle') {
        payload.buy_menu_product_id = buy_menu_product_id;
        payload.buy_quantity = Number(buy_quantity);
        payload.get_menu_product_id = get_menu_product_id;
        payload.get_quantity = Number(get_quantity);
    } else {
        payload.discount_value = Number(discount_value);
        payload.product_ids = product_ids;
    }

    return payload;
}

function validateStep(step, formData) {
    const { name, type, discount_value, buy_menu_product_id, buy_quantity,
        get_menu_product_id, get_quantity, schedule, product_ids } = formData;

    if (step === 1) {
        if (!name.trim()) return 'Nome é obrigatório';
        return null;
    }

    if (step === 2) {
        if (type === 'bundle') {
            if (!buy_menu_product_id) return 'Selecione o produto comprado';
            if (!buy_quantity || Number(buy_quantity) < 1) return 'Informe a quantidade mínima';
            if (!get_menu_product_id) return 'Selecione o produto grátis';
            if (!get_quantity || Number(get_quantity) < 1) return 'Informe a quantidade grátis';
        } else {
            if (!discount_value || Number(discount_value) <= 0) return 'Informe o valor do desconto';
        }
        return null;
    }

    if (step === 3) {
        const s = schedule;
        if (s.schedule_type === 'one_time') {
            if (!s.starts_at) return 'Informe o início';
            if (!s.ends_at) return 'Informe o fim';
        } else {
            if (!s.days_of_week?.length) return 'Selecione ao menos um dia';
            if (!s.time_start) return 'Informe o horário de início';
            if (!s.time_end) return 'Informe o horário de fim';
        }
        return null;
    }

    if (step === 4) {
        if (!product_ids?.length) return 'Selecione ao menos um produto';
        return null;
    }

    return null;
}

export default function PromotionSlideOver({ isOpen, onClose, onSaved, slug, initialData }) {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [step, setStep] = useState(1);
    const [stepError, setStepError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const isDesktop = useMediaQuery(`(min-width: ${breakpoints.lg})`);
    const isBundle = formData.type === 'bundle';
    const totalSteps = isBundle ? 3 : 4;
    const isEdit = !!initialData;

    useEffect(() => {
        if (isOpen) {
            setFormData(initialData ? promotionToFormData(initialData) : EMPTY_FORM);
            setStep(1);
            setStepError(null);
            setSaveError(null);
        }
    }, [isOpen, initialData]);

    function handleNext() {
        const err = validateStep(step, formData);
        if (err) { setStepError(err); return; }
        setStepError(null);
        setStep(s => s + 1);
    }

    function handleBack() {
        setStepError(null);
        setSaveError(null);
        setStep(s => s - 1);
    }

    async function handleSave() {
        const err = validateStep(step, formData);
        if (err) { setStepError(err); return; }
        setStepError(null);
        setSaving(true);
        setSaveError(null);
        try {
            const payload = buildPayload(formData);
            let saved;
            if (isEdit) {
                const { data } = await promotionsApi.update(slug, initialData.id, payload);
                saved = data;
            } else {
                const { data } = await promotionsApi.create(slug, payload);
                saved = data;
            }
            onSaved(saved);
        } catch (err) {
            const msg = err?.response?.data?.message || 'Erro ao salvar. Tente novamente.';
            setSaveError(msg);
        } finally {
            setSaving(false);
        }
    }

    const STEP_LABELS = isBundle
        ? ['Informações', 'Desconto', 'Agendamento']
        : ['Informações', 'Desconto', 'Agendamento', 'Produtos'];

    const isLastStep = step === totalSteps;

    if (!isOpen) return null;

    const content = (
        <>
            <PanelHeader>
                <HeaderLeft>
                    {step > 1 && (
                        <BackBtn onClick={handleBack} disabled={saving}>
                            <ArrowLeft size={18} />
                        </BackBtn>
                    )}
                    <HeaderTitle>
                        {isEdit ? 'Editar promoção' : 'Nova promoção'}
                    </HeaderTitle>
                </HeaderLeft>
                <CloseBtn onClick={() => !saving && onClose()}>
                    <X size={20} color={colors.night} />
                </CloseBtn>
            </PanelHeader>

            <StepIndicator>
                {STEP_LABELS.map((label, i) => {
                    const n = i + 1;
                    const done = n < step;
                    const active = n === step;
                    return (
                        <React.Fragment key={n}>
                            <StepItem
                                $clickable={isEdit && !active}
                                onClick={isEdit && !active ? () => { setStepError(null); setStep(n); } : undefined}
                            >
                                <StepDot $done={done} $active={active}>
                                    {done ? '✓' : n}
                                </StepDot>
                                <StepLabel $active={active}>{label}</StepLabel>
                            </StepItem>
                            {i < STEP_LABELS.length - 1 && (
                                <StepConnector $done={done} />
                            )}
                        </React.Fragment>
                    );
                })}
            </StepIndicator>

            <PanelContent $noScroll={step === 4 && !isBundle}>
                {step === 1 && (
                    <StepBasicInfo data={formData} onChange={setFormData} />
                )}
                {step === 2 && (
                    <StepDiscount data={formData} onChange={setFormData} slug={slug} />
                )}
                {step === 3 && (
                    <StepSchedule data={formData} onChange={setFormData} />
                )}
                {step === 4 && !isBundle && (
                    <StepProducts
                        data={formData}
                        onChange={setFormData}
                        slug={slug}
                        editingId={initialData?.id}
                    />
                )}
            </PanelContent>

            {(stepError || saveError) && (
                <ErrorBanner>{stepError || saveError}</ErrorBanner>
            )}

            <PanelFooter>
                {isLastStep ? (
                    <SaveBtn onClick={handleSave} disabled={saving}>
                        {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar promoção'}
                    </SaveBtn>
                ) : (
                    <NextBtn onClick={handleNext}>
                        Próximo
                    </NextBtn>
                )}
            </PanelFooter>
        </>
    );

    if (isDesktop) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={() => !saving && onClose()}
                size='large'
                closeButton={false}
                childrenOverflowY='scroll'
            >
                <DesktopPanelBody>{content}</DesktopPanelBody>
            </Modal>
        );
    }

    return (
        <>
            <Overlay onClick={() => !saving && onClose()} />
            <Panel>{content}</Panel>
        </>
    );
}

const slideIn = keyframes`
    from { transform: translateX(-100%); }
    to   { transform: translateX(0); }
`;

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 100;
    backdrop-filter: blur(2px);
`;

const Panel = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    max-width: 480px;
    background: #fff;
    z-index: 101;
    display: flex;
    flex-direction: column;
    animation: ${slideIn} 0.28s cubic-bezier(0.32, 0.72, 0, 1);
    box-shadow: 8px 0 40px rgba(0, 0, 0, 0.14);

    @media screen and (max-width: ${breakpoints.sm}) {
        max-width: 100%;
    }
`;

const DesktopPanelBody = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
    flex-shrink: 0;
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const HeaderTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const BackBtn = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: ${colors.neutralDark};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: ${colors.nightDark};
    transition: background 0.15s;
    &:hover { background: #e0e0e5; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const CloseBtn = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: none;
    background: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.15s;
    &:hover { background: ${colors.neutralDark}; }
`;

const StepIndicator = styled.div`
    display: flex;
    align-items: center;
    padding: 16px 24px;
    border-bottom: 1px solid ${colors.neutralDark};
    flex-shrink: 0;
    overflow-x: auto;
    gap: 0;
    &::-webkit-scrollbar { display: none; }
`;

const StepItem = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
    cursor: ${({ $clickable }) => $clickable ? 'pointer' : 'default'};
    &:hover > span { color: ${({ $clickable }) => $clickable ? panelAccent(colors.occasionalPurple) : 'inherit'}; }
`;

const StepDot = styled.div`
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    font-weight: 600;
    transition: all 0.2s;
    background: ${({ $done, $active }) =>
        $done ? colors.occasionalGreen :
        $active ? panelAccent(colors.occasionalPurple) : colors.neutralDark};
    color: ${({ $done, $active }) => ($done || $active) ? '#fff' : colors.nightLight};
`;

const StepLabel = styled.span`
    font-size: 10px;
    font-weight: ${({ $active }) => $active ? '700' : '500'};
    color: ${({ $active }) => $active ? panelAccent(colors.occasionalPurple) : colors.nightLight};
    white-space: nowrap;
`;

const StepConnector = styled.div`
    flex: 1;
    height: 2px;
    min-width: 20px;
    margin: 0 4px;
    margin-bottom: 18px;
    background: ${({ $done }) => $done ? colors.occasionalGreen : colors.neutralDark};
    transition: background 0.2s;
`;

const PanelContent = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow-y: ${({ $noScroll }) => $noScroll ? 'hidden' : 'auto'};
    padding: 24px 24px 8px;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: ${colors.neutralDark}; border-radius: 10px; }
`;

const ErrorBanner = styled.div`
    margin: 0 24px 4px;
    padding: 10px 14px;
    background: ${colors.danger}0A;
    border: 1px solid ${colors.danger}30;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: ${colors.danger};
    flex-shrink: 0;
`;

const PanelFooter = styled.div`
    padding: 16px 24px 24px;
    border-top: 1px solid ${colors.neutralDark};
    flex-shrink: 0;
`;

const NextBtn = styled.button`
    width: 100%;
    padding: 14px;
    border-radius: 12px;
    background: ${panelAccent(colors.occasionalPurple)};
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    border: none;
    cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.9; }
`;

const SaveBtn = styled.button`
    width: 100%;
    padding: 14px;
    border-radius: 12px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : colors.occasionalGreen};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 15px;
    font-weight: 600;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.9; }
`;
